import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type {
    AddOpportunitiesToListDto,
    AddOpportunitiesToListResponseDto,
    CreateOpportunityListDto,
    CreateOpportunityListResponse,
    ExcludeListsDuplicateOpportunitiesDto,
    ExcludeListsDuplicateOpportunitiesResponseDto,
    ListRecommendationsRequestParams,
    ListRecommendationType,
    MoveOpportunitiesToListDto,
    MoveToListResponseDto,
    OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import {
    OpportunityListVariant,
} from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { IFetchService } from "app/services/fetchService";

import { BASE_OPPORTUNITY_LIST_URL } from "./const";
import createCrmListApiService from "./crmListApiService";
import createStaticListsApiService from "./staticListsApiService";
import type { RevealContactsParams } from "./types";
import { createStaticListUrl } from "./utils";

export const createOpportunitiesApiService = (
    fetchService: IFetchService,
    excelDownloadClient: ExcelDownloadClient,
) => {
    const staticList = createStaticListsApiService(fetchService, excelDownloadClient);
    const crmList = createCrmListApiService(fetchService, excelDownloadClient);

    return {
        staticList,
        crmList,

        revealTopNContacts({ listParams, leadsType, dto }: RevealContactsParams) {
            switch (listParams.variant) {
                case OpportunityListVariant.Crm: {
                    return crmList[leadsType].revealTopNContactsFromCrmList(listParams.id, dto);
                }

                case OpportunityListVariant.Static: {
                    return staticList[leadsType].revealTopNContacts(listParams.id, dto);
                }
            }
        },

        fetchListRecommendations(params: ListRecommendationsRequestParams): Promise<ListRecommendationType[]> {
            return fetchService.get("/sales-api/workspace-data/recommendations", params);
        },

        createOpportunityList(dto: CreateOpportunityListDto): Promise<CreateOpportunityListResponse> {
            const { name, country, domains } = dto;

            return fetchService.post(
                `${BASE_OPPORTUNITY_LIST_URL}?listName=${encodeURIComponent(name)}&country=${country}`,
                domains,
            );
        },

        updateOpportunityList(
            listId: OpportunityListType["opportunityListId"],
            dto: Partial<Pick<OpportunityListType, "country" | "friendlyName">>,
        ): Promise<OpportunityListType> {
            return fetchService.patch(createStaticListUrl(listId), dto);
        },

        deleteOpportunityList(listId: OpportunityListType["opportunityListId"]): Promise<void> {
            return fetchService.delete(createStaticListUrl(listId));
        },

        addOpportunitiesToList(
            listId: OpportunityListType["opportunityListId"],
            dto: AddOpportunitiesToListDto,
        ): Promise<AddOpportunitiesToListResponseDto> {
            return fetchService.put(createStaticListUrl(listId, "/opportunities"), dto);
        },

        moveOpportunitiesToList(
            listId: OpportunityListType["opportunityListId"],
            dto: MoveOpportunitiesToListDto,
            includeRegionals: boolean,
        ): Promise<MoveToListResponseDto> {
            return fetchService.post(
                createStaticListUrl(listId, `/move-opportunities?includeRegionals=${includeRegionals}`),
                dto,
            );
        },

        removeOpportunitiesFromList(
            listId: OpportunityListType["opportunityListId"],
            domains: readonly string[],
            includeRegionals: boolean,
        ): Promise<void> {
            return fetchService.post(
                createStaticListUrl(listId, `/opportunities/delete?includeRegionals=${includeRegionals}`),
                domains,
            );
        },

        fetchExcludeListDuplicatesForStaticList(
            dto: ExcludeListsDuplicateOpportunitiesDto,
        ): Promise<ExcludeListsDuplicateOpportunitiesResponseDto> {
            return fetchService.post(`${BASE_OPPORTUNITY_LIST_URL}/opportunities/check-duplicates`, dto);
        },

        dismissRecommendation(listId: OpportunityListType["opportunityListId"], domain: string): Promise<void> {
            return fetchService.post(createStaticListUrl(listId, `/black-list/${domain}`), {});
        },
    };
};
